import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import SearchableSelect from '../components/SearchableSelect';
import ModuleOverlay from '../components/ModuleOverlay';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const bankAccLabel = (a) => `${a.account_name} — ${a.bank_name}`;
const bankAccSubLabel = (a) => {
  const parts = [];
  if (a.branch_name) parts.push(a.branch_name);
  if (a.iban) parts.push(a.iban);
  else if (a.account_number) parts.push(a.account_number);
  parts.push(a.currency_code || 'TRY');
  return parts.filter(Boolean).join(' | ');
};
const bankAccSearchKeys = ['account_name', 'bank_name', 'branch_name', 'iban', 'account_number'];

const TX_CONFIG = {
  opening: { label: 'Banka Açılış Fişleri',     color: '#7c3aed', sign:  1 },
  credit:  { label: 'Banka Alacak Dekontları',  color: '#16a34a', sign:  1 },
  debit:   { label: 'Banka Borç Dekontları',    color: '#dc2626', sign: -1 },
  virman:  { label: 'Banka Virman Dekontları',  color: '#0284c7', sign: -1 },
};

const LOCAL_CURRENCY = 'TRY';

const emptyTx = {
  transaction_date: new Date().toISOString().substring(0, 10),
  description: '', amount: '', reference_no: '', notes: '', counterparty: '',
  account_id: '', target_account_id: '',
  source_rate: '1', target_rate: '1', target_amount: '',
  personnel_id: '', special_code1: '', special_code2: '',
};

function fmt(n, cur) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + (cur ? ' ' + cur : '');
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

// modalMode: null | 'view' | 'edit' | 'add'
export default function BankVoucherPage({ txType, autoOpenId = null, onNavigate = null }) {
  const cfg = TX_CONFIG[txType] || TX_CONFIG.credit;
  const isVirman = txType === 'virman';

  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', bank_account_id: '', source: '' });

  const [modalMode, setModalMode] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [txForm, setTxForm] = useState({ ...emptyTx });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [quickAddTarget, setQuickAddTarget] = useState(null);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [showQuickList, setShowQuickList] = useState(null); // 'special1' | 'special2' | null

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: txType };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/bank-accounts/all-transactions', { params });
      if (res.data.success) setTransactions(res.data.data || []);
    } catch (err) {
      console.error('all-transactions error:', err.response?.data || err.message);
    } finally { setLoading(false); }
  }, [txType, filters]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/bank-accounts');
      if (res.data.success) setAccounts((res.data.data.items || []).filter(a => a.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  // Deep-link: autoOpenId varsa o hareketi aç (transactions yüklendikten sonra)
  useEffect(() => {
    if (!autoOpenId) return;
    const tx = (transactions || []).find(t => t.id === autoOpenId);
    if (tx) openView(tx);
  }, [autoOpenId, transactions]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => {
    api.get('/personnel', { params: { limit: 200 } }).then(r => {
      if (r.data.success) setPersonnel((r.data.data.items || []).filter(p => p.is_active));
    }).catch(() => {});
  }, []);

  const fetchSpecialCodes = useCallback(async () => {
    try {
      const r = await api.get('/invoices/lookups/all');
      if (r.data.success) {
        setSpecialCodes1(r.data.data?.specialCodes1 || []);
        setSpecialCodes2(r.data.data?.specialCodes2 || []);
      }
    } catch {}
  }, []);
  useEffect(() => { fetchSpecialCodes(); }, [fetchSpecialCodes]);

  const fetchRateForCurrency = async (currencyCode, date) => {
    if (!currencyCode || currencyCode === LOCAL_CURRENCY) return '1';
    try {
      const res = await api.get('/exchange/rates/lookup', { params: { date, currency_code: currencyCode } });
      if (res.data.success && res.data.data) {
        const r = res.data.data;
        return String(r.mid_rate || r.buying_rate || r.selling_rate || '1');
      }
    } catch {}
    return '1';
  };

  const recalcTargetAmount = (amount, sourceRate, targetRate) => {
    const a = parseFloat(amount) || 0;
    const sr = parseFloat(sourceRate) || 0;
    const tr = parseFloat(targetRate) || 0;
    if (a > 0 && sr > 0 && tr > 0) return (a * sr / tr).toFixed(2);
    return '';
  };

  const closeModal = () => { setModalMode(null); setSelectedTx(null); setMsg(null); };

  const buildFormFromTx = (tx) => {
    const dateStr = tx.transaction_date
      ? new Date(tx.transaction_date).toISOString().substring(0, 10)
      : emptyTx.transaction_date;
    const srcAcc = accounts.find(a => a.id === tx.account_id);
    const tgtAcc = accounts.find(a => a.id === tx.linked_account_id);
    const cross = isVirman && srcAcc && tgtAcc && srcAcc.currency_code !== tgtAcc.currency_code;
    const srcAmt = String(tx.amount || '');
    const tgtAmt = tx.linked_amount != null ? String(tx.linked_amount) : '';
    const tgtRate = cross && parseFloat(srcAmt) > 0 && parseFloat(tgtAmt) > 0
      ? (parseFloat(tgtAmt) / parseFloat(srcAmt)).toFixed(6)
      : '1';
    return {
      ...emptyTx,
      transaction_date: dateStr,
      description: tx.description || '',
      amount: srcAmt,
      reference_no: tx.reference_no || '',
      notes: tx.notes || '',
      counterparty: tx.counterparty || '',
      account_id: tx.account_id,
      target_account_id: tx.linked_account_id || '',
      target_amount: tgtAmt,
      source_rate: srcAcc?.currency_code === LOCAL_CURRENCY ? '1' : '1',
      target_rate: tgtRate,
    };
  };

  // Tahsilat veya başka modülden gelen otomatik kayıtlara tıklandığında kaynak belgeye yönlendir.
  const SOURCE_ROUTES = {
    cari_receipt:    'cari-tahsilat',
    cari_payment:    'cari-odeme',
    pos_slip:        'bank-pos-slips',
    expense_voucher: 'expense-vouchers',
  };
  // Çek/senet için yön + tip kombinasyonuna göre doğru sayfa
  const resolveCheckRoute = (docType, direction) => {
    if (docType === 'check'  && direction === 'incoming') return 'cek-portfoy';
    if (docType === 'check'  && direction === 'outgoing') return 'cek-verilen';
    if (docType === 'note'   && direction === 'incoming') return 'senet-portfoy';
    if (docType === 'note'   && direction === 'outgoing') return 'senet-verilen';
    return 'cek-portfoy';
  };

  const openView = (tx) => {
    if (tx.source_type === 'checks_notes' && tx.source_id && onNavigate) {
      onNavigate(resolveCheckRoute(tx.source_check_doc_type, tx.source_check_direction), tx.source_id);
      return;
    }
    if (tx.source_type && tx.source_id && onNavigate && SOURCE_ROUTES[tx.source_type]) {
      onNavigate(SOURCE_ROUTES[tx.source_type], tx.source_id);
      return;
    }
    setTxForm(buildFormFromTx(tx));
    setSelectedTx(tx);
    setMsg(null);
    setModalMode('view');
  };

  const openAdd = () => {
    setTxForm({ ...emptyTx });
    setSelectedTx(null);
    setMsg(null);
    setModalMode('add');
  };

  const handleSourceAccountChange = async (accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    const cur = acc?.currency_code;
    let sourceRate = '1';
    if (cur && cur !== LOCAL_CURRENCY) sourceRate = await fetchRateForCurrency(cur, txForm.transaction_date);
    setTxForm(prev => {
      const nf = { ...prev, account_id: accountId, source_rate: sourceRate };
      nf.target_amount = recalcTargetAmount(nf.amount, sourceRate, nf.target_rate);
      return nf;
    });
  };

  const handleTargetAccountChange = async (accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    const cur = acc?.currency_code;
    let targetRate = '1';
    if (cur && cur !== LOCAL_CURRENCY) targetRate = await fetchRateForCurrency(cur, txForm.transaction_date);
    setTxForm(prev => {
      const nf = { ...prev, target_account_id: accountId, target_rate: targetRate };
      nf.target_amount = recalcTargetAmount(nf.amount, nf.source_rate, targetRate);
      return nf;
    });
  };

  const handleDateChange = async (date) => {
    const srcAcc = accounts.find(a => a.id === txForm.account_id);
    const tgtAcc = accounts.find(a => a.id === txForm.target_account_id);
    const [sourceRate, targetRate] = await Promise.all([
      srcAcc?.currency_code && srcAcc.currency_code !== LOCAL_CURRENCY
        ? fetchRateForCurrency(srcAcc.currency_code, date) : Promise.resolve(txForm.source_rate),
      tgtAcc?.currency_code && tgtAcc.currency_code !== LOCAL_CURRENCY
        ? fetchRateForCurrency(tgtAcc.currency_code, date) : Promise.resolve(txForm.target_rate),
    ]);
    setTxForm(prev => {
      const nf = { ...prev, transaction_date: date, source_rate: sourceRate, target_rate: targetRate };
      nf.target_amount = recalcTargetAmount(nf.amount, sourceRate, targetRate);
      return nf;
    });
  };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    const { account_id, target_account_id, transaction_date, description, amount, target_amount } = txForm;
    if (!account_id) return setMsg({ type: 'error', text: 'Hesap secin.' });
    if (isVirman && modalMode === 'add' && !target_account_id) return setMsg({ type: 'error', text: 'Hedef hesap secin.' });
    if (!transaction_date || !description || !amount) return setMsg({ type: 'error', text: 'Tarih, aciklama ve tutar zorunludur.' });
    if (modalMode === 'add' && isCrossCurrency && !target_amount) return setMsg({ type: 'error', text: 'Hedef tutar zorunludur.' });
    setSaving(true); setMsg(null);
    try {
      const body = {
        transaction_date,
        description,
        amount: parseFloat(amount),
        reference_no: txForm.reference_no || null,
        counterparty: txForm.counterparty || null,
        notes: txForm.notes || null,
        personnel_id: txForm.personnel_id || null,
        special_code1: txForm.special_code1 || null,
        special_code2: txForm.special_code2 || null,
      };
      if (modalMode === 'edit') {
        if (isCrossCurrency && target_amount) body.target_amount = parseFloat(target_amount);
        await api.put('/bank-accounts/' + account_id + '/transactions/' + selectedTx.id, body);
      } else {
        body.transaction_type = isVirman ? 'virman' : txType;
        if (isVirman) {
          body.target_account_id = target_account_id;
          if (isCrossCurrency && target_amount) body.target_amount = parseFloat(target_amount);
        }
        await api.post('/bank-accounts/' + account_id + '/transactions', body);
      }
      await fetchTransactions();
      if (mode === 'close') closeModal();
      else if (mode === 'new') { setMsg({ type: 'success', text: 'Kaydedildi — yeni fiş' }); setTimeout(() => setMsg(null), 2000); }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi.' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedTx) return;
    if (!window.confirm('Bu islemi silmek istiyor musunuz?')) return;
    try {
      await api.delete('/bank-accounts/' + selectedTx.account_id + '/transactions/' + selectedTx.id);
      closeModal();
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.message || 'Silinemedi.');
    }
  };

  const tf = (key, val) => setTxForm(prev => ({ ...prev, [key]: val }));

  const sourceAccount = accounts.find(a => a.id === txForm.account_id);
  const targetAccount = accounts.find(a => a.id === txForm.target_account_id);
  const isCrossCurrency = isVirman && sourceAccount && targetAccount &&
    sourceAccount.currency_code !== targetAccount.currency_code;
  const srcIsLocal = sourceAccount?.currency_code === LOCAL_CURRENCY;
  const tgtIsLocal = targetAccount?.currency_code === LOCAL_CURRENCY;

  const filtered = transactions.filter(tx => {
    // Search filtresi
    const s = (search || '').toLowerCase();
    const matchesSearch = !s ||
      (tx.document_no || '').toLowerCase().includes(s) ||
      tx.description?.toLowerCase().includes(s) ||
      tx.account_name?.toLowerCase().includes(s) ||
      tx.bank_name?.toLowerCase().includes(s) ||
      (tx.reference_no || '').toLowerCase().includes(s) ||
      (tx.counterparty || '').toLowerCase().includes(s);
    if (!matchesSearch) return false;
    // Kaynak filtresi
    const src = filters.source;
    if (src === 'manual'      && tx.source_type)            return false;
    if (src === 'auto'        && !tx.source_type)           return false;
    if (src === 'cari_receipt'    && tx.source_type !== 'cari_receipt')    return false;
    if (src === 'cari_payment'    && tx.source_type !== 'cari_payment')    return false;
    if (src === 'pos_slip'        && tx.source_type !== 'pos_slip')        return false;
    if (src === 'checks_notes'    && tx.source_type !== 'checks_notes')    return false;
    if (src === 'expense_voucher' && tx.source_type !== 'expense_voucher') return false;
    return true;
  });

  const ro = modalMode === 'view'; // read-only

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inView = modalMode === 'view';
  const isAdd = modalMode === 'add';
  const isEdit = modalMode === 'edit';
  const inEditAdd = isAdd || isEdit;

  const modalTitle = (modalMode === 'add' ? 'Yeni: ' : '') + cfg.label.replace('Banka ', '');

  return (
    <div className="stocks-page">
      <ListPageHeader
        title={cfg.label}
        count={filtered.length}
        unit="kayit"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ara..."
        onRefresh={fetchTransactions}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Dekont</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to',   type: 'date', label: 'Son Tarih' },
          { key: 'bank_account_id', type: 'select', label: 'Banka Hesabı', flex: '1 1 280px', minWidth: 240,
            options: [{ value: '', label: 'Tüm Hesaplar' }, ...accounts.map(a => ({ value: a.id, label: `${a.account_name} — ${a.bank_name} (${a.currency_code})` }))] },
          { key: 'source', type: 'select', label: 'Kaynak', flex: '1 1 200px', minWidth: 180,
            options: [
              { value: '',             label: 'Tümü' },
              { value: 'manual',       label: '✏ Sadece Manuel Girişler' },
              { value: 'cari_receipt',    label: '🔗 Cari Tahsilat Kaynaklı' },
              { value: 'cari_payment',    label: '💸 Cari Ödeme Kaynaklı' },
              { value: 'pos_slip',        label: '💳 POS Slip Tahsilatı' },
              { value: 'checks_notes',    label: '📜 Çek / Senet' },
              { value: 'expense_voucher', label: '💸 Gider Fişi' },
              { value: 'auto',            label: '⚡ Tüm Otomatik Üretilen' },
            ] },
        ]}
      />

      {/* LİSTE */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey={`bank-voucher-${txType}-list`}
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={tx => openView(tx)}
          columns={[
            { id: 'doc_no', header: 'Belge No', accessor: 'document_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>{tx.document_no || '—'}</span> },
            { id: 'date', header: 'Tarih', accessor: 'transaction_date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.transaction_date||0)-new Date(b.transaction_date||0),
              cell: tx => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(tx.transaction_date)}</span> },
            { id: 'account', header: 'Hesap', accessor: tx => (tx.account_name||'')+' '+(tx.bank_name||''), sortable: true, width: 180,
              cell: tx => <><div style={{ fontWeight: 600 }}>{tx.account_name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{tx.bank_name}</div></> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: tx => {
                const srcLabels = { cari_receipt: 'Cari Tahsilat', cari_payment: 'Cari Ödeme', pos_slip: 'POS Slip', checks_notes: tx.source_check_doc_type === 'note' ? 'Senet' : 'Çek', expense_voucher: 'Gider Fişi' };
                const isAuto = !!tx.source_type;
                return (
                  <>
                    <div style={{ display:'flex', alignItems:'center', gap: 6, flexWrap:'wrap' }}>
                      {isAuto && (
                        <span title={`Bu kayıt otomatik üretildi — tıklayarak kaynağa git`}
                              style={{ fontSize:'0.66rem', fontWeight: 700, padding:'1px 7px', borderRadius: 100,
                                       background:'rgba(124,58,237,0.10)', color:'#7c3aed',
                                       border:'1px solid rgba(124,58,237,0.25)', whiteSpace:'nowrap', letterSpacing:'0.04em', textTransform:'uppercase' }}>
                          🔗 {srcLabels[tx.source_type] || tx.source_type}
                        </span>
                      )}
                      <span>{tx.description}</span>
                    </div>
                    {tx.counterparty && <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{tx.counterparty}</div>}
                    {tx.notes && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>{tx.notes}</div>}
                  </>
                );
              } },
            ...(isVirman ? [
              { id: 'target_account', header: 'Hedef Hesap', accessor: 'linked_account_name', sortable: true, width: 160,
                cell: tx => <span className="stock-secondary"><div>{tx.linked_account_name || '—'}</div>{tx.linked_currency_code && tx.linked_currency_code !== tx.currency_code && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{tx.linked_currency_code}</div>}</span> },
            ] : []),
            { id: 'ref_no', header: 'Ref No', accessor: 'reference_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{tx.reference_no || '—'}</span> },
            ...(isVirman ? [
              { id: 'src_amount', header: 'Kaynak Tutar', accessor: tx => parseFloat(tx.amount)||0, sortable: true, width: 140,
                headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' },
                cell: tx => `-${fmt(tx.amount, tx.currency_code)}` },
              { id: 'dst_amount', header: 'Hedef Tutar', accessor: tx => parseFloat(tx.linked_amount)||0, sortable: true, width: 140,
                headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' },
                cell: tx => tx.linked_amount != null ? '+' + fmt(tx.linked_amount, tx.linked_currency_code) : '—' },
            ] : [
              { id: 'amount', header: 'Tutar', accessor: tx => parseFloat(tx.amount)||0, sortable: true, width: 140,
                headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' },
                cell: tx => <span style={{ color: cfg.color }}>{cfg.sign > 0 ? '+' : '-'}{fmt(tx.amount, tx.currency_code)}</span> },
            ]),
            { id: 'detail', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 64,
              cellStyle: { textAlign: 'center' },
              cell: tx => {
                if (!tx.source_type || !tx.source_id || !onNavigate) return null;
                let route = null;
                if (tx.source_type === 'checks_notes') route = resolveCheckRoute(tx.source_check_doc_type, tx.source_check_direction);
                else route = SOURCE_ROUTES[tx.source_type];
                if (!route) return null;
                const titleMap = { cari_receipt: 'Tahsilat Makbuzunu Aç', cari_payment: 'Ödeme Makbuzunu Aç', pos_slip: 'POS Slip\'i Aç', checks_notes: (tx.source_check_doc_type === 'note' ? 'Senedi' : 'Çeki') + ' Aç', expense_voucher: 'Gider Fişini Aç' };
                return (
                  <button onClick={e => { e.stopPropagation(); onNavigate(route, tx.source_id); }}
                          title={titleMap[tx.source_type] || 'Kaynak belgeyi aç'}
                          style={{ padding:'3px 8px', fontSize:'0.78rem', borderRadius: 6, border:'1px solid var(--border-secondary)', background:'var(--bg-card)', cursor:'pointer', color:'var(--accent-primary)' }}>
                    📋 Detay
                  </button>
                );
              } },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: tx => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/bank-accounts/transactions/${tx.id}/audit`} /></span> },
          ]}
        />
      </div>

      {/* FİŞ / DEKONT MODAL */}
      {modalMode !== null && (
        <Modal
          icon={null}
          title={modalTitle}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEditAdd && <Button variant="ghost" onClick={isEdit ? () => { setMsg(null); setModalMode('view'); } : closeModal}>{isAdd ? 'Vazgeç' : 'İptal'}</Button>}
                {inEditAdd && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setMsg(null); setModalMode('edit'); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {modalMode !== 'add' && selectedTx && (
            <CardHero
              icon="🏦"
              code={selectedTx.doc_number}
              title={selectedTx.bank_name || 'Banka Dekontu'}
              subtitle={selectedTx.date ? new Date(selectedTx.date).toLocaleDateString('tr-TR') : null}
              kpis={[
                selectedTx.amount != null && {
                  label: 'Tutar', value: fmt(selectedTx.amount, selectedTx.currency_code),
                  color: 'primary', sublabel: selectedTx.currency_code,
                },
              ].filter(Boolean)}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>

            {/* BELGE NO */}
            <FormField label="Belge No">
              <input
                disabled
                value={modalMode === 'add' ? 'Otomatik' : (selectedTx?.document_no || '—')}
                style={{ fontFamily: 'monospace', opacity: 0.7 }}
              />
            </FormField>

            {/* HESAP ALANLARI */}
            <FormField label={isVirman ? 'Kaynak Hesap' : 'Hesap'} required={!ro}>
              {(ro || modalMode === 'edit') ? (
                <input disabled value={sourceAccount ? `${sourceAccount.account_name} — ${sourceAccount.bank_name} (${sourceAccount.currency_code})` : (txForm.account_id || '—')} />
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <SearchableSelect
                      value={txForm.account_id}
                      onChange={id => handleSourceAccountChange(id)}
                      options={accounts}
                      getLabel={bankAccLabel}
                      getSubLabel={bankAccSubLabel}
                      searchKeys={bankAccSearchKeys}
                      placeholder="Hesap seç — ad, banka, IBAN ile ara"
                    />
                  </div>
                  <Button variant="ghost" size="sm" title="Yeni banka hesabı" onClick={() => setQuickAddTarget('source')}>+</Button>
                </div>
              )}
            </FormField>

            {isVirman ? (
              <FormField label="Hedef Hesap" required={!ro}>
                {(ro || modalMode === 'edit') ? (
                  <input disabled value={targetAccount ? `${targetAccount.account_name} — ${targetAccount.bank_name} (${targetAccount.currency_code})` : (txForm.target_account_id || '—')} />
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelect
                        value={txForm.target_account_id}
                        onChange={id => handleTargetAccountChange(id)}
                        options={accounts.filter(a => a.id !== txForm.account_id)}
                        getLabel={bankAccLabel}
                        getSubLabel={bankAccSubLabel}
                        searchKeys={bankAccSearchKeys}
                        placeholder="Hedef hesap seç — ad, banka, IBAN ile ara"
                      />
                    </div>
                    <Button variant="ghost" size="sm" title="Yeni banka hesabı" onClick={() => setQuickAddTarget('target')}>+</Button>
                  </div>
                )}
              </FormField>
            ) : (
              <FormField label="Karsi Taraf">
                <input disabled={ro} value={txForm.counterparty} onChange={e => tf('counterparty', e.target.value)} placeholder={ro ? '' : 'Firma veya kisi adi'} />
              </FormField>
            )}

            {/* TARİH */}
            <FormField label="Tarih" required={!ro}>
              <input type={ro ? 'text' : 'date'}
                disabled={ro}
                value={ro ? fmtDate(txForm.transaction_date) : txForm.transaction_date}
                onChange={e => modalMode === 'add' ? handleDateChange(e.target.value) : tf('transaction_date', e.target.value)}
              />
            </FormField>

            <FormField label={isCrossCurrency ? `Kaynak Tutar (${sourceAccount?.currency_code})` : 'Tutar'} required={!ro}>
              <input type={ro ? 'text' : 'number'} min="0" step="0.01"
                disabled={ro}
                placeholder={ro ? '' : '0.00'}
                value={ro ? fmt(txForm.amount) : txForm.amount}
                onChange={e => {
                  const amount = e.target.value;
                  setTxForm(prev => ({
                    ...prev, amount,
                    ...(isCrossCurrency ? { target_amount: recalcTargetAmount(amount, prev.source_rate, prev.target_rate) } : {}),
                  }));
                }}
              />
            </FormField>

            {/* CROSS-CURRENCY */}
            {isCrossCurrency && <>
              <FormField label={<>Kaynak Kur — 1 {sourceAccount.currency_code} = ? TRY{srcIsLocal && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>(yerel)</span>}</>}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.000001"
                  disabled={ro || srcIsLocal}
                  placeholder={ro ? '' : '0.000000'}
                  value={txForm.source_rate}
                  onChange={e => {
                    const sourceRate = e.target.value;
                    setTxForm(prev => ({ ...prev, source_rate: sourceRate, target_amount: recalcTargetAmount(prev.amount, sourceRate, prev.target_rate) }));
                  }}
                />
              </FormField>
              <FormField label={<>Hedef Kur — 1 {targetAccount.currency_code} = ? TRY{tgtIsLocal && <span style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>(yerel)</span>}</>}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.000001"
                  disabled={ro || tgtIsLocal}
                  placeholder={ro ? '' : '0.000000'}
                  value={txForm.target_rate}
                  onChange={e => {
                    const targetRate = e.target.value;
                    setTxForm(prev => ({ ...prev, target_rate: targetRate, target_amount: recalcTargetAmount(prev.amount, prev.source_rate, targetRate) }));
                  }}
                />
              </FormField>
              <FormField label={`Hedef Tutar (${targetAccount.currency_code})`} required={!ro}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.01"
                  disabled={ro}
                  placeholder={ro ? '' : '0.00'}
                  value={ro ? fmt(txForm.target_amount) : txForm.target_amount}
                  onChange={e => {
                    const tgt = parseFloat(e.target.value) || 0;
                    const amt = parseFloat(txForm.amount) || 0;
                    const sr = parseFloat(txForm.source_rate) || 0;
                    setTxForm(prev => ({
                      ...prev, target_amount: e.target.value,
                      ...(amt > 0 && tgt > 0 && sr > 0 && !tgtIsLocal ? { target_rate: (amt * sr / tgt).toFixed(6) } : {}),
                    }));
                  }}
                />
              </FormField>
              {!ro && (
                <FormField label="Çevrim Özeti" fullWidth>
                  <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--r-md)', padding: '8px 12px', fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span><strong style={{ color: 'var(--danger)' }}>{txForm.amount || '0'} {sourceAccount.currency_code}</strong>{txForm.source_rate && txForm.source_rate !== '1' && <span style={{ color: 'var(--text-tertiary)' }}> × {txForm.source_rate} TRY</span>}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                    {(() => { const t = (parseFloat(txForm.amount)||0)*(parseFloat(txForm.source_rate)||0); return t > 0 ? <span><strong>{fmt(t, 'TRY')}</strong>{txForm.target_rate && txForm.target_rate !== '1' && <span style={{ color: 'var(--text-tertiary)' }}> ÷ {txForm.target_rate} TRY</span>}</span> : null; })()}
                    <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                    <span><strong style={{ color: 'var(--success)' }}>{txForm.target_amount || '0'} {targetAccount.currency_code}</strong></span>
                  </div>
                </FormField>
              )}
            </>}

            {/* ORTAK ALANLAR */}
            <FormField label="Açıklama" required={!ro} fullWidth>
              <input disabled={ro} value={txForm.description} onChange={e => tf('description', e.target.value)} placeholder={ro ? '' : 'İşlem aciklamasi'} />
            </FormField>
            <FormField label="Referans No">
              <input disabled={ro} value={txForm.reference_no} onChange={e => tf('reference_no', e.target.value)} placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>
            <FormField label="Not">
              <input disabled={ro} value={txForm.notes} onChange={e => tf('notes', e.target.value)} placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>
            <FormField label="Personel">
              <select value={txForm.personnel_id || ''} onChange={e => tf('personnel_id', e.target.value)} disabled={ro}>
                <option value="">Seçiniz</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 1 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special1')}>+</button>}</>}>
              <select disabled={ro} value={txForm.special_code1 || ''} onChange={e => tf('special_code1', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes1.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 {!ro && <button type="button" className="quick-add-btn" onClick={() => setShowQuickList('special2')}>+</button>}</>}>
              <select disabled={ro} value={txForm.special_code2 || ''} onChange={e => tf('special_code2', e.target.value)}>
                <option value="">Seçiniz</option>
                {specialCodes2.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
          </div>
        </Modal>
      )}

      {quickAddTarget !== null && (
        <ModuleOverlay
          type="banka"
          onClose={() => setQuickAddTarget(null)}
          onSaved={async (rec) => {
            await fetchAccounts();
            if (rec?.id) {
              if (quickAddTarget === 'source') handleSourceAccountChange(rec.id);
              else handleTargetAccountChange(rec.id);
            }
          }}
        />
      )}

      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase="/invoices/special-codes"
          extraBody={{ code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={fetchSpecialCodes}
        />
      )}
    </div>
  );
}
